import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Package, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { apiErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WorkerPicker from "@/components/op/WorkerPicker";
import OpHeader from "@/components/op/OpHeader";
import { SampleTable } from "@/components/SamplePanel";

const todayWita = () => new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 10);
const emptyRow = () => ({ description: "", qty: "1", due_date: "", notes: "" });
const num = (n) => new Intl.NumberFormat("id-ID").format(n || 0);

export default function Contoh() {
  const [worker, setWorker] = useState(null);
  const [takenDate, setTakenDate] = useState(todayWita());
  const [items, setItems] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const idemRef = useRef(crypto.randomUUID());

  const load = useCallback(() => {
    api.get("/op/contoh?status=DIPINJAM").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setItem = (i, k, v) => setItems((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const submit = async () => {
    if (!worker) return toast.error("Pilih pengepul dulu");
    const v = [];
    for (const it of items) {
      if (!it.description.trim() && !(Number(it.qty) > 0)) continue;
      if (!it.description.trim()) return toast.error("Nama / deskripsi barang contoh wajib diisi");
      if (!(Number(it.qty) > 0)) return toast.error("Jumlah harus lebih dari 0");
      v.push({ description: it.description.trim(), qty: Number(it.qty), due_date: it.due_date || null, notes: it.notes.trim() || null });
    }
    if (!v.length) return toast.error("Isi minimal satu barang contoh");
    setSaving(true);
    try {
      const { data } = await api.post("/op/contoh/bulk", { worker_id: worker.worker_id, taken_date: takenDate || null, items: v });
      toast.success(`${data.length} contoh dicatat · pengingat aktif di ${worker.worker_id}`);
      idemRef.current = crypto.randomUUID();
      setItems([emptyRow()]); setWorker(null);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="w-full pb-10" data-testid="op-contoh">
      <OpHeader title="CATAT CONTOH / SAMPEL" />

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm space-y-5" data-testid="contoh-form">
        <p className="text-sm text-slate-500">
          Catat barang contoh/sampel yang dibawa pengepul — bisa banyak barang sekaligus. Nama barang diketik manual.
          Contoh BUKAN transaksi: tidak masuk WIP, Jurnal, maupun Totalan — hanya pengingat sampai dikembalikan. Data ini
          masuk ke menu Contoh/Sampel di ledger utama.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WorkerPicker value={worker} onChange={setWorker} />
          <div>
            <Label>Tanggal dibawa</Label>
            <Input type="date" value={takenDate} onChange={(e) => setTakenDate(e.target.value)} className="h-11 mt-1" data-testid="contoh-taken" />
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-12 gap-2 px-1 text-[11px] font-bold text-slate-400 uppercase">
            <div className="col-span-6">Nama / Deskripsi barang *</div>
            <div className="col-span-2">Jumlah</div>
            <div className="col-span-3">Jatuh tempo (ops.)</div>
            <div className="col-span-1"></div>
          </div>
          {items.map((it, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`contoh-row-${i}`}>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6"><Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="mis. contoh jahitan mika pendek merah" className="h-10" data-testid={`contoh-desc-${i}`} /></div>
                <div className="col-span-2"><Input type="number" min="1" inputMode="numeric" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="h-10" data-testid={`contoh-qty-${i}`} /></div>
                <div className="col-span-3"><Input type="date" value={it.due_date} onChange={(e) => setItem(i, "due_date", e.target.value)} className="h-10" data-testid={`contoh-due-${i}`} /></div>
                <div className="col-span-1 flex justify-center"><button onClick={() => setItems((a) => a.length > 1 ? a.filter((_, j) => j !== i) : a)} className="p-2 text-rose-500" data-testid={`contoh-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
              </div>
              <Input value={it.notes} onChange={(e) => setItem(i, "notes", e.target.value)} placeholder="Catatan (opsional)" className="h-9" data-testid={`contoh-notes-${i}`} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setItems((a) => [...a, emptyRow()])} data-testid="contoh-add"><Plus className="w-4 h-4 mr-1" />Tambah Barang</Button>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="text-sm text-slate-500">Total <b className="text-slate-900">{items.length}</b> barang · <b className="text-slate-900">{num(items.reduce((a, x) => a + Number(x.qty || 0), 0))}</b> PCS</div>
          <Button onClick={submit} disabled={saving || !worker} className="rounded-full bg-[#0071E3] h-11 px-6" data-testid="contoh-submit">
            <Package className="w-4 h-4 mr-2" />Catat Contoh
          </Button>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid="contoh-list">
        <div className="font-semibold text-slate-800 mb-3">Contoh Belum Dikembalikan ({rows.length})</div>
        <SampleTable rows={rows} onDone={load} compact opMode />
      </div>
    </div>
  );
}
